import { Container, Box, Typography, makeStyles } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

const EFFECTIVE_DATE = "July 22, 2026";

const useStyles = makeStyles((theme) => ({
  "@keyframes heroFadeUp": {
    from: { opacity: 0, transform: "translateY(24px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  heroSection: {
    position: "relative",
    overflow: "hidden",
    background:
      "linear-gradient(135deg, #05070f 0%, #101632 50%, #05070f 100%)",
    textAlign: "center",
    padding: "52px 20px 44px",
  },
  heroLogo: {
    width: 76,
    height: 76,
    borderRadius: 20,
    display: "block",
    margin: "0 auto 16px",
    boxShadow: "0 14px 36px rgba(0,0,0,0.5)",
    animation: "$heroFadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) both",
  },
  heroTitle: {
    fontSize: "clamp(1.4rem, 3.2vw, 1.9rem)",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 6,
    animation: "$heroFadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both",
  },
  heroSub: {
    fontSize: "0.84rem",
    color: "rgba(255,255,255,0.7)",
    animation: "$heroFadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both",
  },
  pageWrapper: {
    background: "#f2f6f4",
    padding: "36px 0 56px",
  },
  policyCard: {
    background: "#ffffff",
    borderRadius: 20,
    border: "1px solid rgba(12,59,44,0.07)",
    boxShadow: "0 10px 40px rgba(12,59,44,0.07)",
    padding: "36px 40px",
    maxWidth: 780,
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: { padding: "26px 22px" },
  },
  effective: {
    fontSize: "0.74rem",
    fontWeight: 600,
    color: "#1d8f66",
    textTransform: "uppercase",
    letterSpacing: "1.6px",
    marginBottom: 20,
  },
  h2: {
    fontSize: "1.02rem",
    fontWeight: 700,
    color: "#0c3b2c",
    margin: "26px 0 8px",
  },
  p: {
    fontSize: "0.86rem",
    color: "#444444",
    lineHeight: 1.75,
    marginBottom: 10,
    overflowWrap: "anywhere",
  },
  ul: {
    margin: "0 0 10px",
    paddingLeft: 22,
    "& li": {
      fontSize: "0.86rem",
      color: "#444444",
      lineHeight: 1.75,
      marginBottom: 5,
    },
  },
  a: {
    color: "#1d8f66",
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline", color: "#0c3b2c" },
  },
  backRow: {
    display: "flex",
    justifyContent: "center",
    marginTop: 30,
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "#0c3b2c",
    background: "#ffffff",
    border: "1px solid rgba(12,59,44,0.2)",
    borderRadius: 11,
    padding: "10px 22px",
    textDecoration: "none",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#0c3b2c",
      textDecoration: "none",
    },
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 26px rgba(12,59,44,0.14)",
    },
    "& svg": { fontSize: 16 },
  },
}));

export const FrightGridPrivacy = () => {
  const classes = useStyles();
  return (
    <div>
      <Helmet>
        <title>FrightGrid Privacy Policy | Arawinz Soft Solutions</title>
        <meta
          name="description"
          content="Privacy policy for FrightGrid: Dark Worlds, the turn based maze puzzle game by Arawinz Soft Solutions, covering the Android app."
        />
      </Helmet>

      <Box className={classes.heroSection}>
        <img
          src="/static/products/frightgrid.png"
          alt="FrightGrid: Dark Worlds"
          className={classes.heroLogo}
        />
        <Typography className={classes.heroTitle}>
          FrightGrid Privacy Policy
        </Typography>
        <Typography className={classes.heroSub}>
          Arawinz Soft Solutions Pvt. Ltd.
        </Typography>
      </Box>

      <Box className={classes.pageWrapper}>
        <Container maxWidth="md">
          <Box className={classes.policyCard}>
            <Typography className={classes.effective}>
              Effective date: {EFFECTIVE_DATE}
            </Typography>

            <Typography className={classes.p}>
              This privacy policy describes how FrightGrid: Dark Worlds ("the
              game"), published by Arawinz Soft Solutions Pvt. Ltd. ("we",
              "us"), handles information on Android.
            </Typography>

            <Typography className={classes.h2}>The short version</Typography>
            <ul className={classes.ul}>
              <li>The game does not ask for an account and does not collect your name, email, phone number, contacts, or location.</li>
              <li>Gameplay works fully offline. All progress is stored on your device only.</li>
              <li>We do not use any analytics or crash-reporting services.</li>
              <li>Ads are served by Google AdMob, which processes some device data under Google's own privacy policy.</li>
              <li>We do not sell your data. We never see it in the first place.</li>
            </ul>

            <Typography className={classes.h2}>
              Data stored on your device
            </Typography>
            <Typography className={classes.p}>
              The game saves your progress locally on your device: settings
              (sound, music, volume levels, vibration), which levels you have
              finished with your best move counts, the last level you played,
              and counters that limit how often ads can appear. This data never
              leaves your device. You can erase it at any time with "Reset
              progress" in the game's settings, or by uninstalling the game.
            </Typography>

            <Typography className={classes.h2}>Advertising (Google AdMob)</Typography>
            <Typography className={classes.p}>
              The game shows ads through Google AdMob: optional rewarded ads
              (to get a hint or to skip a level you are stuck on, both entirely
              your choice) and an occasional interstitial after a level ends.
              To serve and measure ads, Google may process device information
              such as your device's advertising identifier, IP address, and ad
              interaction data. This processing is governed by{" "}
              <a
                className={classes.a}
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google's Privacy Policy
              </a>
              . Where required by law, the game shows a consent form before ads
              are personalized, and you can change your choice there. You can
              also reset or delete your advertising identifier in your device
              settings (Android: Settings, Google, Ads).
            </Typography>

            <Typography className={classes.h2}>Permissions</Typography>
            <Typography className={classes.p}>
              On Android the game requests a single permission: internet
              access, used only for ads. The game does not request location,
              camera, microphone, storage, or contact permissions.
            </Typography>

            <Typography className={classes.h2}>Children</Typography>
            <Typography className={classes.p}>
              The game is spooky and cute rather than violent, is rated for a
              general audience, and does not knowingly collect personal
              information from anyone, including children. Ads are served
              through Google AdMob in line with Google's advertising policies.
            </Typography>

            <Typography className={classes.h2}>
              Data sharing and selling
            </Typography>
            <Typography className={classes.p}>
              We do not sell, rent, or share personal information. The only
              third party involved is Google AdMob (advertising), as described
              above.
            </Typography>

            <Typography className={classes.h2}>Deleting your data</Typography>
            <Typography className={classes.p}>
              All game progress lives on your device: use "Reset progress" in
              the game's settings or uninstall the game to remove it.
              Advertising data held by Google can be managed through your
              Google account settings.
            </Typography>

            <Typography className={classes.h2}>Changes to this policy</Typography>
            <Typography className={classes.p}>
              If we change how the game handles data, we will update this page
              and its effective date before the change takes effect.
            </Typography>

            <Typography className={classes.h2}>Contact</Typography>
            <Typography className={classes.p}>
              Questions about this policy or your data? Email us at{" "}
              <a className={classes.a} href="mailto:contact@arawinz.com">
                contact@arawinz.com
              </a>{" "}
              (Arawinz Soft Solutions Pvt. Ltd.).
            </Typography>
          </Box>

          <Box className={classes.backRow}>
            <Link to="/product/fright-grid" className={classes.backBtn}>
              <ArrowBackIcon /> Back to FrightGrid
            </Link>
          </Box>
        </Container>
      </Box>
    </div>
  );
};
